import React from "react";
import { Handle, Position } from "react-flow-renderer";

/**
 * Conectores de un nodo, en los CUATRO lados (0419 F3-UX).
 *
 * POR QUE: hasta ahora cada recuadro solo enganchaba por arriba y por abajo. En un flujo con
 * menus de seis opciones eso obliga a que todas las aristas bajen en paralelo y se crucen, y
 * el lienzo se vuelve ilegible. Con entrada y salida por los cuatro lados, la arista sale por
 * donde tiene sentido.
 *
 * ── LO QUE NO SE PUEDE TOCAR ────────────────────────────────────────────────────────────────
 *
 * El motor rutea por `sourceHandle`. `fn_flowbuilder_get_next_node` tiene dos ramas:
 *
 *   - Preguntado con handle NULL  → ignora `sourceHandle` y toma cualquier arista del nodo.
 *   - Preguntado con un handle    → exige que `sourceHandle` coincida (o sea NULL).
 *
 * De ahi salen las dos reglas de este componente, y no son negociables:
 *
 *   1. Nodos de UNA salida (texto, espera, HTTP, imagen…): el SP pregunta con NULL, asi que el
 *      `sourceHandle` que se guarde da igual. Pueden llevar salida por los cuatro lados.
 *   2. Nodos de VARIAS salidas (menu, condicion, botones): el SP pregunta con el valor de la
 *      opcion o con 'true'/'false'. Sus ids son SEMANTICOS y este componente no los emite —
 *      esos nodos siguen dibujando sus propios <Handle> con el id que les toca.
 *
 * Las ENTRADAS son libres en todos los casos: el motor no lee `targetHandle` en ningun sitio.
 */

/**
 * COMPATIBILIDAD, y no es un detalle: los flujos ya guardados tienen `sourceHandle: null` y
 * `targetHandle: null`. Si TODOS los conectores llevaran id, esas aristas se quedarian sin
 * handle al que agarrarse y habria que fiarse de como resuelve React Flow un id nulo —que es
 * codigo minificado de terceros—.
 *
 * Por eso el conector que HOY es el de por defecto no lleva id: entrada ARRIBA, salida ABAJO.
 * Los otros tres son los nuevos. Asi un flujo antiguo se dibuja exactamente igual que antes,
 * por construccion y no por suerte.
 */
const LADOS_ENTRADA = [
  { id: undefined, position: Position.Top, estilo: { top: -6 } },
  { id: "t-right", position: Position.Right, estilo: { right: -6 } },
  { id: "t-bottom", position: Position.Bottom, estilo: { bottom: -6 } },
  { id: "t-left", position: Position.Left, estilo: { left: -6 } }
];

const LADOS_SALIDA = [
  { id: undefined, position: Position.Bottom, estilo: { bottom: -6 } },
  { id: "s-top", position: Position.Top, estilo: { top: -6 } },
  { id: "s-right", position: Position.Right, estilo: { right: -6 } },
  { id: "s-left", position: Position.Left, estilo: { left: -6 } }
];

/**
 * @param {string}  color      color del borde, para que el conector case con el nodo
 * @param {boolean} entrada    dibuja los conectores de ENTRADA (por defecto si)
 * @param {boolean} salida     dibuja los de SALIDA. Ponlo a `false` en nodos de varias salidas
 *                             y en los terminales (saltar a otro flujo).
 */
const NodePorts = ({ color = "#888", entrada = true, salida = true }) => {
  const base = {
    width: 11,
    height: 11,
    background: "white",
    border: `2px solid ${color}`,
    borderRadius: "50%"
  };

  return (
    <>
      {entrada &&
        LADOS_ENTRADA.map((l) => (
          <Handle
            key={l.id || "t-default"}
            type="target"
            id={l.id}
            position={l.position}
            style={{ ...base, ...l.estilo }}
          />
        ))}
      {salida &&
        LADOS_SALIDA.map((l) => (
          <Handle
            key={l.id || "s-default"}
            type="source"
            id={l.id}
            position={l.position}
            style={{ ...base, ...l.estilo, opacity: 0.85 }}
          />
        ))}
    </>
  );
};

export default NodePorts;
